// ============================================
// SmsCampaignForm.jsx - CHILD COMPONENT (FULL FILE)
// ============================================

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useFieldArray, Controller, useFormContext } from "react-hook-form";
import { MDBBtn, MDBCard, MDBCardBody, MDBIcon, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalFooter, MDBModalHeader, MDBModalTitle } from "mdb-react-ui-kit";

const SmsCampaignForm = React.memo(({
  campaignMisc,
  draftDetails,
  websiteForReplacement,
  brandNameForReplacement,
  allLocations,
  billingGroupList
}) => {
  // Get useForm parameters from FormProvider
  const {
    register,
    control,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  // Watch form values
  const selectedSubcases = watch("campaign_details.usecases.subcase") || [];

  const sessioncompanyName =
    sessionStorage.getItem("admin_session") &&
      JSON.parse(sessionStorage.getItem("admin_session"))?.companyName
      ? JSON.parse(sessionStorage.getItem("admin_session")).companyName
      : "";

  const companyname = sessioncompanyName;

  // Local state for UI management
  const [activeEditor, setActiveEditor] = useState(null);
  const [justShook, setJustShook] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  // Create mode: show Campaign Description hint as placeholder (not actual form value)
  const [descPlaceholder, setDescPlaceholder] = useState("");
  const [isDescFocused, setIsDescFocused] = useState(false);
  let flowChecked = watch("campaign_details.camp_desc.msg_flow_chk");

  // Field arrays for dynamic content
  const {
    fields: sampleMsgFields,
    append: appendSampleMsg,
    remove: removeSampleMsg,
  } = useFieldArray({
    control,
    name: "campaign_details.camp_desc.smpl_msgs",
  });

  // OLD: Array-based consent field array (commented out — consent_msg is now a single string)
  // const {
  //   fields: consentMsgFields,
  //   append: appendConsentMsg,
  //   remove: removeConsentMsg,
  // } = useFieldArray({
  //   control,
  //   name: "campaign_details.camp_desc.consent_msg",
  // });

  // Get use cases and consent types from campaignMisc prop
  const useCases = campaignMisc?.sub_usecases || [];
  // OLD: const consentTypes = campaignMisc?.consentMsg || [];

  const isDraftMode = draftDetails && Object.keys(draftDetails).length > 0;

  // Refs for tracking previous values and replacement position markers
  const previousWebsiteRef = useRef("[customerwebsite]");
  const previousBrandNameRef = useRef("[CustomerName]");
  const usecaseReplaceText = useRef(""); // Store the current use cases text in the description
  // OLD: const consentMsgCache = useRef({}); // Cache consent message values when toggling off/on (array approach)

  // Initial useEffect - Set default values only for NEW campaigns
  useEffect(() => {
    if (!isDraftMode && !isInitialized && campaignMisc?.staticTexts) {
      // Get currently selected use cases
      const currentSubcases = watch("campaign_details.usecases.subcase") || [];

      // Calculate use cases string
      const useCaseNames = currentSubcases
        .map(useCaseId => {
          const useCase = useCases.find(uc => uc.usecase_id === useCaseId);
          return useCase ? useCase.usecase : null;
        })
        .filter(name => name !== null);

      const useCasesString = useCaseNames.join(", ");

      // Replace REPLACE_USECASES with the use cases string (empty if no use cases)
      const initialDesc = campaignMisc.staticTexts.camp_desc?.replace(
        "REPLACE_USECASES",
        useCasesString
      ) || "";

      // Create mode: show as placeholder only, not as actual form value
      setDescPlaceholder(initialDesc);

      // Store the initial use cases string in the ref
      usecaseReplaceText.current = useCasesString;

      // Replace [CustomerName] with brandNameForReplacement in opt-in message
      setValue(
        "campaign_details.camp_desc.optin_desc",
        campaignMisc.staticTexts.opt_in_msg?.replace(
          "[CustomerName]",
          brandNameForReplacement
        ) || "",
        { shouldDirty: true }
      );

      // Replace [CustomerName] with brandNameForReplacement in opt-out message
      setValue(
        "campaign_details.camp_desc.optout_desc",
        campaignMisc.staticTexts.opt_out_msg?.replace(
          "[CustomerName]",
          brandNameForReplacement
        ) || "",
        { shouldDirty: true }
      );

      // Replace both [Customer Name] and [customerwebsite] in help message
      // FIXED: Handle both patterns - with and without protocol
      const helpMsg = (campaignMisc.staticTexts.help_msg || "")
        .replace("[Customer Name]", brandNameForReplacement)
        // First replace the pattern with protocol (https://[customerwebsite] or http://[customerwebsite])
        .replace(/https?:\/\/\[customerwebsite\]/g, `https://${websiteForReplacement}`)
        // Then replace standalone [customerwebsite] if it exists elsewhere
        .replace(/\[customerwebsite\]/g, websiteForReplacement);

      setValue(
        "campaign_details.camp_desc.help_desc",
        helpMsg,
        { shouldDirty: true }
      );

      // Store the initial values in refs
      previousWebsiteRef.current = websiteForReplacement;
      previousBrandNameRef.current = brandNameForReplacement;

      setIsInitialized(true);
    }
  }, [campaignMisc, companyname, isDraftMode, setValue, isInitialized, websiteForReplacement, brandNameForReplacement, watch, useCases]);

  // CRITICAL FIX: Set isInitialized to true for draft mode
  // Without this, the replacement logic never runs because isInitialized stays false
  useEffect(() => {
    if (isDraftMode && !isInitialized) {
      setIsInitialized(true);
    }
  }, [isDraftMode, isInitialized]);

  // Draft mode: set descPlaceholder from template once campaignMisc becomes available and desc is empty.
  // Runs separately from the init above because campaignMisc arrives asynchronously (parent fetches it)
  // and may not be populated yet when the draft-init effect fires.
  useEffect(() => {
    if (isDraftMode && campaignMisc?.staticTexts?.camp_desc) {
      const currentDesc = watch("campaign_details.camp_desc.desc") || "";
      if (!currentDesc) {
        const currentSubcases = watch("campaign_details.usecases.subcase") || [];
        const useCaseNames = currentSubcases
          .map(useCaseId => {
            const useCase = useCases.find(uc => uc.usecase_id === useCaseId);
            return useCase ? useCase.usecase : null;
          })
          .filter(name => name !== null);
        const useCasesString = useCaseNames.join(", ");
        setDescPlaceholder(
          campaignMisc.staticTexts.camp_desc.replace("REPLACE_USECASES",useCasesString)
        );
        usecaseReplaceText.current = useCasesString;
      }
    }
  }, [campaignMisc, isDraftMode]);

  // Dynamic replacement useEffect - Handle changes to use cases with simple pattern matching
  useEffect(() => {
    if (isInitialized && !isDraftMode) {
      // Calculate current use cases string from selected subcases
      const currentSubcases = watch("campaign_details.usecases.subcase") || [];
      const currentUseCaseNames = currentSubcases
        .map(useCaseId => {
          const useCase = useCases.find(uc => uc.usecase_id === useCaseId);
          return useCase ? useCase.usecase : null;
        })
        .filter(name => name !== null);

      const newUseCasesString = currentUseCaseNames.join(", ");

      // Only proceed if use cases have changed
      if (newUseCasesString !== usecaseReplaceText.current) {
        const currentDesc = watch("campaign_details.camp_desc.desc") || "";
        const beforeText = "The campaign is designed to send subscribers ";
        const afterText = " content.";

        if (currentDesc === "") {
          // User hasn't typed anything yet — just update the placeholder text
          setDescPlaceholder(
            (campaignMisc?.staticTexts?.camp_desc || "").replace("REPLACE_USECASES", newUseCasesString)
          );
          usecaseReplaceText.current = newUseCasesString;
        } else if (currentDesc.startsWith(beforeText)) {
          // User typed something and it still matches the auto-generated pattern — update it
          const expectedPattern = beforeText + usecaseReplaceText.current + afterText;

          if (currentDesc.startsWith(expectedPattern)) {
            const remainingText = currentDesc.substring(expectedPattern.length);
            const updatedDesc = beforeText + newUseCasesString + afterText + remainingText;

            setValue("campaign_details.camp_desc.desc", updatedDesc, {
              shouldDirty: true,
            });

            usecaseReplaceText.current = newUseCasesString;
          }
          // If pattern doesn't match, user has modified the text - skip replacement
        }
        // If beforeText not at start, user modified beginning - skip replacement
      }
    } else if (isDraftMode && campaignMisc?.staticTexts?.camp_desc) {
      // Draft mode: update placeholder when use cases change and desc is empty
      const currentDesc = watch("campaign_details.camp_desc.desc") || "";
      if (!currentDesc) {
        const currentSubcases = watch("campaign_details.usecases.subcase") || [];
        const currentUseCaseNames = currentSubcases
          .map(useCaseId => {
            const useCase = useCases.find(uc => uc.usecase_id === useCaseId);
            return useCase ? useCase.usecase : null;
          })
          .filter(name => name !== null);
        const newUseCasesString = currentUseCaseNames.join(", ");
        setDescPlaceholder(
          campaignMisc.staticTexts.camp_desc.replace("REPLACE_USECASES", newUseCasesString)
        );
        usecaseReplaceText.current = newUseCasesString;
      }
    }
  }, [selectedSubcases, isInitialized, isDraftMode, setValue, watch, useCases]);

  // Dynamic replacement useEffect - Handle changes to website and brand name
  useEffect(() => {
    if (isInitialized) {
      const websiteChanged = websiteForReplacement !== previousWebsiteRef.current;
      const brandNameChanged = brandNameForReplacement !== previousBrandNameRef.current;

      // Store previous values before any updates
      const prevWebsite = previousWebsiteRef.current;
      const prevBrandName = previousBrandNameRef.current;

      // CRITICAL FIX: Smart initialization check for draft mode
      // We need to distinguish between:
      // 1. NEW FORM: Text has placeholders, needs replacement when user enters value
      // 2. DRAFT: Text already has real values, just sync ref without replacement
      if (prevBrandName === "[CustomerName]" && brandNameForReplacement !== "[CustomerName]") {
        // Check if form text still contains the placeholder
        const currentOptinDesc = watch("campaign_details.camp_desc.optin_desc") || "";
        const currentOptoutDesc = watch("campaign_details.camp_desc.optout_desc") || "";
        const currentHelpDesc = watch("campaign_details.camp_desc.help_desc") || "";

        const hasPlaceholder =
          currentOptinDesc.includes("[CustomerName]") ||
          currentOptoutDesc.includes("[CustomerName]") ||
          currentHelpDesc.includes("[Customer Name]"); // Help uses different format

        if (!hasPlaceholder) {
          // DRAFT MODE: Form text already has real brand name (no placeholder found)
          // Just sync the ref without doing replacement
          previousBrandNameRef.current = brandNameForReplacement;

          // Also sync website ref if needed (for draft mode)
          if (prevWebsite === "[customerwebsite]" && websiteForReplacement !== "[customerwebsite]") {
            previousWebsiteRef.current = websiteForReplacement;
          }

          return; // Exit early, no replacement needed
        }
        // If placeholder exists, continue to replacement logic below (NEW FORM case)
      }

      // Separate check for website initialization in drafts
      if (prevWebsite === "[customerwebsite]" && websiteForReplacement !== "[customerwebsite]") {
        const currentHelpDesc = watch("campaign_details.camp_desc.help_desc") || "";

        // Check if help description has website placeholder
        const hasWebsitePlaceholder =
          currentHelpDesc.includes("[customerwebsite]") ||
          currentHelpDesc.includes("https://[customerwebsite]") ||
          currentHelpDesc.includes("http://[customerwebsite]");

        if (!hasWebsitePlaceholder) {
          // DRAFT MODE: Just sync ref without replacement
          previousWebsiteRef.current = websiteForReplacement;
          // Don't return - continue checking other conditions
        }
        // If placeholder exists, continue to replacement logic
      }

      // Update opt-in description (brand name replacement)
      if (brandNameChanged) {
        const currentOptinDesc = watch("campaign_details.camp_desc.optin_desc") || "";
        const updatedOptinDesc = currentOptinDesc.replace(
          prevBrandName,
          brandNameForReplacement
        );

        setValue("campaign_details.camp_desc.optin_desc", updatedOptinDesc, {
          shouldDirty: true,
        });

        // Update opt-out description (brand name replacement)
        const currentOptoutDesc = watch("campaign_details.camp_desc.optout_desc") || "";
        const updatedOptoutDesc = currentOptoutDesc.replace(
          prevBrandName,
          brandNameForReplacement
        );

        setValue("campaign_details.camp_desc.optout_desc", updatedOptoutDesc, {
          shouldDirty: true,
        });
      }

      // Update help description (both website and brand name replacement)
      if (websiteChanged || brandNameChanged) {
        const currentHelpDesc = watch("campaign_details.camp_desc.help_desc") || "";
        let updatedHelpDesc = currentHelpDesc;

        if (websiteChanged) {
          // Build full URL with https:// prefix
          const oldFullUrl = `https://${prevWebsite}`;
          const newFullUrl = `https://${websiteForReplacement}`;

          // Only replace if the old URL actually exists in the text
          // This prevents issues with debouncing and initialization
          if (currentHelpDesc.includes(oldFullUrl)) {
            updatedHelpDesc = updatedHelpDesc.replace(oldFullUrl, newFullUrl);
          } else {
            // Old URL not found - this means ref wasn't synced properly
            // Try to find and replace any https:// URL in the text
            const urlMatch = currentHelpDesc.match(/https:\/\/[^\s]+/);
            if (urlMatch) {
              updatedHelpDesc = updatedHelpDesc.replace(urlMatch[0], newFullUrl);
            }
          }
        }

        if (brandNameChanged) {
          // Simple string replacement for brand name (same as old code)
          updatedHelpDesc = updatedHelpDesc.replace(prevBrandName, brandNameForReplacement);
        }

        setValue("campaign_details.camp_desc.help_desc", updatedHelpDesc, {
          shouldDirty: true,
        });
      }

      // Update refs at the end after all replacements are done
      if (websiteChanged) {
        previousWebsiteRef.current = websiteForReplacement;
      }
      if (brandNameChanged) {
        previousBrandNameRef.current = brandNameForReplacement;
      }
    }
  }, [websiteForReplacement, brandNameForReplacement, isInitialized, setValue, watch]);

  // Field configurations for text areas
  const textFieldConfigs = [
    {
      id: "desc",
      title: "Campaign Description (Minimum 40 Characters):",
      display: "Campaign Description",
      minLen: 40,
      path: "campaign_details.camp_desc.desc",
      maxLen: 4096,
    },
    {
      id: "optin_desc",
      title: "Opt-In Process Description:",
      display: "Opt-In Process Description",
      minLen: 20,
      path: "campaign_details.camp_desc.optin_desc",
      maxLen: 1024,
    },
    {
      id: "optout_desc",
      title: "Opt-Out Process Description:",
      display: "Opt-Out Process Description",
      minLen: 20,
      path: "campaign_details.camp_desc.optout_desc",
      maxLen: 1024,
    },
    {
      id: "help_desc",
      title: "Help Process Description:",
      display: "Help Process Description",
      minLen: 20,
      path: "campaign_details.camp_desc.help_desc",
      maxLen: 1024,
    },
  ];


  // Initialize default 2 sample messages if none exist
  useEffect(() => {
    if (sampleMsgFields.length === 0 && !isDraftMode) {
      appendSampleMsg({ id: 1, msg_txt: "" }, { shouldFocus: false });
      appendSampleMsg({ id: 2, msg_txt: "" }, { shouldFocus: false });
    }
  }, [sampleMsgFields.length, appendSampleMsg, isDraftMode]);

  // Handle Add Sample Message
  const handleAddSampleMessage = () => {
    if (sampleMsgFields.length >= 5) {
      setJustShook("sample-messages");
      setTimeout(() => setJustShook(null), 500);
      return;
    }

    const newId = sampleMsgFields.length + 1;
    appendSampleMsg({ id: newId, msg_txt: "" }, { shouldFocus: false });
  };

  // Handle Remove Sample Message with reindexing
  const handleRemoveSampleMessage = (index) => {
    if (sampleMsgFields.length <= 2) {
      return;
    }

    removeSampleMsg(index);

    // Reindex remaining messages
    setTimeout(() => {
      const currentMessages = watch("campaign_details.camp_desc.smpl_msgs") || [];
      const reindexedMessages = currentMessages.map((msg, idx) => ({
        ...msg,
        id: idx + 1,
      }));
      setValue("campaign_details.camp_desc.smpl_msgs", reindexedMessages, {
        shouldDirty: true,
      });
    }, 0);
  };

  // Validate that minimum 2 sample messages exist
  useEffect(() => {
    if (sampleMsgFields.length < 2 && !isDraftMode) {
      const needed = 2 - sampleMsgFields.length;
      for (let i = 0; i < needed; i++) {
        appendSampleMsg({ id: sampleMsgFields.length + i + 1, msg_txt: "" }, { shouldFocus: false });
      }
    }
  }, [sampleMsgFields.length, appendSampleMsg, isDraftMode]);

  const manualSampleMsgFields = useMemo(
    () => sampleMsgFields.filter((field) => !field.type),
    [sampleMsgFields]
  );

  const handleRemoveManualSample = (fieldId) => {
    if (manualSampleMsgFields.length <= 1) {
      return;
    }

    const removalIndex = sampleMsgFields.findIndex((field) => field.id === fieldId);
    if (removalIndex !== -1) {
      removeSampleMsg(removalIndex);
    }
  };

  // Toggle use case selection - simplified version without direct replacement
  const toggleUseCase = (useCaseId) => {
    clearErrors("campaign_details.usecases.subcase");
    const currentSubcases = selectedSubcases || [];
    const isSelected = currentSubcases.includes(useCaseId);

    if (isSelected) {
      // Remove the use case
      const newSubcases = currentSubcases.filter((id) => id !== useCaseId);
      setValue("campaign_details.usecases.subcase", newSubcases, {
        shouldDirty: true,
      });

      // Remove corresponding sample message if exists
      const existingMsgIndex = sampleMsgFields.findIndex(
        (msg) => msg.type === useCaseId
      );
      if (existingMsgIndex !== -1) {
        removeSampleMsg(existingMsgIndex);
      }
    } else {
      // Add the use case (max 5)
      if (currentSubcases.length >= 5) {
        setJustShook(useCaseId);
        setTimeout(() => setJustShook(null), 500);
        return;
      }

      const newSubcases = [...currentSubcases, useCaseId];
      setValue("campaign_details.usecases.subcase", newSubcases, {
        shouldDirty: true,
      });
    }
    // The replacement will be handled by the useEffect watching selectedSubcases
  };

  // OLD: Array-based consent type toggle (commented out — consent_msg is now a single string)
  // const toggleConsentType = (consentId) => {
  //   clearErrors("campaign_details.camp_desc.consent_msg");
  //   const existingIndex = consentMsgFields.findIndex(
  //     (msg) => msg.consent_type === consentId
  //   );
  //
  //   if (existingIndex !== -1) {
  //     const currentMsg = consentMsgFields[existingIndex];
  //     if (currentMsg.consent_msg) {
  //       consentMsgCache.current[consentId] = currentMsg.consent_msg;
  //     }
  //     removeConsentMsg(existingIndex);
  //     setValue("campaign_details.camp_desc.consent_msg", [
  //       ...consentMsgFields.filter((_, i) => i !== existingIndex),
  //     ]);
  //   } else {
  //     const consentType = consentTypes.find((ct) => ct.id === consentId);
  //     const consentMessageText = consentMsgCache.current[consentId]
  //       ? consentMsgCache.current[consentId]
  //       : campaignMisc?.staticTexts?.consant_msg
  //         ? campaignMisc.staticTexts.consant_msg.replace(
  //           "REPLACE_CONSENT",
  //           consentType?.msg_type
  //         )
  //         : "";
  //     appendConsentMsg(
  //       { enabled: true, consent_type: consentId, consent_msg: consentMessageText },
  //       { shouldFocus: false }
  //     );
  //   }
  // };

  console.log("errors :::::::: ", errors);

  // Check if field is valid
  const isFieldValid = (path, minLen) => {
    const value = watch(path);
    return (value?.trim().length || 0) >= minLen;
  };

  const [websitecollectModal, setwebsitecollectModal] = useState(false);

  const toggleOpen = () => setwebsitecollectModal(!websitecollectModal);

  return (
    <div className="camp-form biz-form">
      <div className="container">
        {/* Campaign Name */}
        <div className="form-group">
          <label htmlFor="campaign_name">
            Campaign Name
          </label>
          <div className="input-wrapper">
            <input
              id="campaign_name"
              placeholder="Enter Your Campaign Name"
              maxLength={50}
              {...register("campaign_details.campaign_name", {
                minLength: {
                  value: 2,
                  message: "Campaign Name must be at least 2 characters",
                },
                maxLength: {
                  value: 50,
                  message: "Campaign Name cannot exceed 50 characters",
                },
              })}
            />
            <svg
              className="input-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
          {errors?.campaign_details?.campaign_name && (
            <div
              style={{
                color: "#dc3545",
                fontSize: "0.875rem",
                marginTop: "0.25rem",
              }}
            >
              {errors.campaign_details.campaign_name.message}
            </div>
          )}
        </div>
        <MDBCard className="p-0 shadow-0 mt-4">
          <MDBCardBody className="p-4 rounded-5 border border-2 rounded-6 shadow-3-strong">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="mb-0 fw-bold">Pricing Details</h5>
            </div>

            <div className="form-row">
              {/* Location */}
              <div className="form-group">
                 <label htmlFor="location_id">Location<span className="required">*</span></label>
                <div className="input-wrapper">
                  <select
                    id="location_id"
                    className="form-select form-control w-100"
                    {...register("location_id", {
                       required: "Please select location",
                      setValueAs: (value) => Number(value),
                    })}
                  >
                    {allLocations &&
                      allLocations.map((loc) => (
                        <option key={loc.loc_id} value={loc.loc_id}>
                          {loc.loc_name}
                        </option>
                      ))}
                  </select>
                  <svg
                    className="input-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                {errors?.location_id && (
                  <div
                    style={{
                      color: "#dc3545",
                      fontSize: "0.875rem",
                      marginTop: "0.25rem",
                    }}
                  >
                    {errors.location_id.message}
                  </div>
                )}
              </div>
              {/* Billing Group */}
              <div className="form-group">
                <label htmlFor="billing_group_id">Billing Group<span className="required">*</span></label>
                <div className="input-wrapper">
                  <select
                    id="billing_group_id"
                    className="form-select form-control w-100"
                    {...register("billing_group_id", {
                     validate: (value) => billingGroupList.some((b) => b.groupid === Number(value)) || "Billing group is required",
                      setValueAs: (value) => Number(value),
                    })}
                  >
                    {billingGroupList &&
                      billingGroupList.map((bgrp) => (
                        <option key={bgrp.groupid} value={bgrp.groupid}>
                          {bgrp.groupname}
                        </option>
                      ))}
                  </select>
                  <svg
                    className="input-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 10h18M7 15h1m4 0h1M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                {errors?.billing_group_id && (
                  <div
                    style={{
                      color: "#dc3545",
                      fontSize: "0.875rem",
                      marginTop: "0.25rem",
                    }}
                  >
                    {errors.billing_group_id.message}
                  </div>
                )}
              </div>
            </div>

            <div className="biz-form form-group p-0 mt-2 ">
              <div className="form-group w-100 m-0">
                <label htmlFor="volume_select">
                  Estimated Monthly Message Volume{" "}
                  <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <select
                    id="cmp_usage"
                    {...register("campaign_details.camp_desc.cmp_usage", {
                      valueAsNumber: true,
                      required: "Please select campaign usage type",
                    })}
                  >
                    <option value="">Select Usage Type</option>
                    <option value={1}>Low Volume Campaign</option>
                    <option value={2}>Standard Campaign</option>
                  </select>
                  <svg
                    className="input-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                {errors?.campaign_details?.camp_desc?.cmp_usage && (
                  <div
                    style={{
                      color: "#dc3545",
                      fontSize: "0.875rem",
                      marginTop: "0.25rem",
                    }}
                  >
                    {errors.campaign_details.camp_desc.cmp_usage.message}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-3 mt-4">
              {/* NRC */}
              <div className="pricing-row">
                <div className="plan-name">
                  <MDBIcon far icon="file-alt" /> One-Time Registration (NRC)
                </div>
                <div className="plan-details">
                  <ul className="mb-0">
                    <li>Required for all campaigns</li>
                  </ul>
                </div>
                <div className="plan-cost">
                  <span className="cost-amount">$19</span>
                  <span className="cost-period">one-time</span>
                </div>
              </div>

              {/* Volume-based pricing */}
              <Controller
                name="ph_numbers.volume_estimate"
                control={control}
                render={({ field }) => {
                  const volume = field.value || "low";
                  return (
                    <>
                      {/* Low Volume */}
                      <div
                        className={`pricing-row ${volume === "low" ? "highlight" : ""
                          }`}
                      >
                        <div className="plan-name">
                          <MDBIcon far icon="envelope" /> Low Volume Campaign
                        </div>
                        <div className="plan-details">
                          <ul className="mb-0">
                            <li>Max 6,000 messages/month</li>
                            <li>Max 2,000 messages/day</li>
                            <li>Max 75 messages/minute</li>
                          </ul>
                        </div>
                        <div className="plan-cost">
                          <span className="cost-amount">$1.50</span>
                          <span className="cost-period">/month</span>
                        </div>
                      </div>
                      <div className="pricing-row ">
                        <div className="plan-name">
                          <MDBIcon fas icon="bolt" /> Standard Campaign
                        </div>
                        <div className="plan-details">
                          <ul className="mb-0">
                            <li>For campaigns exceeding Low Volume limits</li>
                          </ul>
                        </div>
                        <div className="plan-cost">
                          <span className="cost-amount">$10</span>
                          <span className="cost-period">/month</span>
                        </div>
                      </div>
                    </>
                  );
                }}
              />
            </div>
          </MDBCardBody>
        </MDBCard>

        {/* Use Cases Section */}
        <div className="p-4 border border-2 rounded-6 shadow-3-strong my-4">
          <div className="use-case-section ">
            <div className="section-header">
              <label className="section-title">
                {`Use Case: Select a minimum of ${isNaN(watch("campaign_details.camp_desc.cmp_usage")) ? 1 : watch("campaign_details.camp_desc.cmp_usage")
                  } and a maximum of 5${" "}`}
                <span className="required">*</span>
              </label>
              <div
                className={`selection-counter ${selectedSubcases.length > 0 ? "active" : ""
                  }`}
              >
                <span>{selectedSubcases.length}</span>
                <span>/</span>
                <span>5 selected</span>
              </div>
            </div>

            <div className="use-cases-grid">
              {useCases.map((useCase) => {
                const isSelected = selectedSubcases.includes(
                  useCase.usecase_id
                );
                const isShaking = justShook === useCase.usecase_id;

                return (
                  <button
                    type="button"
                    key={useCase.usecase_id}
                    onClick={() => toggleUseCase(useCase.usecase_id)}
                    className={`use-case-tag ${isSelected ? "selected" : ""} ${isShaking ? "shake" : ""
                      } ${selectedSubcases.length >= 5
                        ? "bg-danger bg-opacity-25"
                        : ""
                      }`}
                  >
                    {useCase.usecase}
                    <MDBIcon
                      fas
                      icon="check"
                      className="check-icon"
                      color="light"
                    />
                  </button>
                );
              })}
            </div>

            {/* Validation for Use Cases - must select at least 1 */}
            <Controller
              name="campaign_details.usecases.subcase"
              control={control}
              rules={{
                validate: (value) => {
                  if (
                    !value ||
                    value.length <
                    (watch("campaign_details.camp_desc.cmp_usage")
                      ? parseInt(
                        watch("campaign_details.camp_desc.cmp_usage")
                      )
                      : 1)
                  ) {
                    return `Please select at least ${watch("campaign_details.camp_desc.cmp_usage")
                      ? parseInt(
                        watch("campaign_details.camp_desc.cmp_usage")
                      ) === 1
                        ? "one"
                        : "two"
                      : "one"
                      } use case`;
                  }
                  return true;
                },
              }}
              render={() => null}
            />
            {errors?.campaign_details?.usecases?.subcase && (
              <div
                style={{
                  color: "#dc3545",
                  fontSize: "0.875rem",
                  marginTop: "0.25rem",
                }}
              >
                {errors.campaign_details.usecases.subcase.message}
              </div>
            )}
          </div>
        </div>


        {/* Options Section */}
        <div className="p-4 border border-2 rounded-6 shadow-3-strong my-4">
          <h5 className="mb-0 fw-bold pb-3"> 
            Content Attributes
          </h5>
        <div className="options-section">
          <div className="options-grid">
            {[
              {
                key: "embed_link",
                label: "Embedded Link",
                path: "campaign_details.embed_link",
              },
              {
                key: "embed_phno",
                label: "Embedded Phone Number",
                path: "campaign_details.embed_phno",
              },
              {
                key: "age_content",
                label: "Age-Gated Content",
                path: "campaign_details.age_content",
              },
            ].map((option) => (
              <div
                key={option.key}
                className="option-group border border-color-secondary border-2 rounded-3 bg-white d-flex align-items-center flex-row justify-content-between"
              >
                <label className="option-label m-0">{option.label}</label>
                <div className="option-buttons">
                  <Controller
                    name={option.path}
                    control={control}
                    render={({ field }) => (
                      <>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={Boolean(field.value)}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                          <span className="slider round"></span>
                        </label>
                      </>
                    )}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        </div>


        {/* Sample Messages Section */}
        <div className="p-4 border border-2 rounded-6 shadow-3-strong mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0 text-start">Sample Messages</h4>
            {sampleMsgFields.length < 5 && (
              <MDBBtn
                className={`primary ${justShook === "sample-messages" ? "shake" : ""}`}
                type="button"
                onClick={handleAddSampleMessage}
              >
                <MDBIcon fas icon="plus" /> Add
              </MDBBtn>
            )}
          </div>

          {sampleMsgFields.map((field, index) => {
            const path = `campaign_details.camp_desc.smpl_msgs.${index}.msg_txt`;
            const value = watch(path) || "";
            const length = value.trim().length;
            const showRemove = sampleMsgFields.length > 2;

            return (
              <div
                key={field.id}
                className="bg-secondary p-3 rounded-3 mt-3 bg-opacity-10 border border-color-secondary border-2"
              >
                <div className="message-section show mb-0">
                  <div className="message-header d-flex justify-content-between align-items-center">
                    <label className="message-label m-0 fw-semibold">
                      Sample Message {index + 1} <span className="required">*</span> (Minimum 20 characters)
                    </label>
                    {showRemove && (
                      <MDBBtn
                        color="danger"
                        outline
                        size="sm"
                        type="button"
                        onClick={() => handleRemoveSampleMessage(index)}
                        className="d-flex align-items-center gap-1"
                      >
                        <MDBIcon fas icon="times" size="lg" /> Remove
                      </MDBBtn>
                    )}
                  </div>
                  <Controller
                    name={path}
                    control={control}
                    rules={{
                      required: "Sample Message is required",
                      minLength: {
                        value: 20,
                        message: "Sample Message must be at least 20 characters",
                      },
                    }}
                    render={({ field }) => (
                      <textarea
                        className="message-textarea"
                        placeholder="Write your sample message"
                        rows={4}
                        maxLength={
                          1024 -
                          ((`Reply STOP to opt-out of receiving messages from [Brand].`
                            .replace(
                              "[Brand]",
                              brandNameForReplacement !== "[CustomerName]"
                                ? brandNameForReplacement
                                : "Brand"
                            )).length + 2)
                        }
                        {...field}
                      />
                    )}
                  />

                  <div className="d-flex justify-content-between mt-2 small text-muted">
                    <span>
                      Minimum 20 characters{" "}
                      {length < 20 ? `(need ${20 - length} more)` : "✓"}
                    </span>
                    <span>{length} characters</span>
                  </div>

                  {errors?.campaign_details?.camp_desc?.smpl_msgs?.[index]
                    ?.msg_txt && (
                      <div
                        style={{
                          color: "#dc3545",
                          fontSize: "0.875rem",
                          marginTop: "0.25rem",
                        }}
                      >
                        {
                          errors.campaign_details.camp_desc.smpl_msgs[index]
                            .msg_txt.message
                        }
                      </div>
                    )}
                </div>
              </div>
            );
          })}

          <div className="bg-secondary p-3 rounded-3 mt-4 bg-opacity-10 border border-color-secondary border-2">
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
              <div>
                <label className="message-label m-0 fw-semibold">
                  {`Reply STOP to opt-out of receiving messages from [Brand].`.replace(
                    "[Brand]",
                    brandNameForReplacement !== "[CustomerName]" ? brandNameForReplacement : "Brand"
                  )}
                </label>
                <p className="text-muted">
                  <b>Note: </b>
                  The above text will be appended with the Sample Message
                </p>
              </div>
            </div>
          </div>

          {/* Validation for minimum 2 sample messages */}
          <Controller
            name="campaign_details.camp_desc.smpl_msgs"
            control={control}
            rules={{
              validate: (value) => {
                if (!Array.isArray(value) || value.length < 2) {
                  return "At least 2 sample messages are required";
                }
                return true;
              },
            }}
            render={() => null}
          />
          {errors?.campaign_details?.camp_desc?.smpl_msgs?.root && (
            <div
              style={{
                color: "#dc3545",
                fontSize: "0.875rem",
                marginTop: "0.25rem",
              }}
            >
              {errors.campaign_details.camp_desc.smpl_msgs.root.message}
            </div>
          )}
        </div>




        <div className="p-4 border border-2 rounded-6 shadow-3-strong mt-4">
          {/* Consent Types Section */}
          <h4 className="mb-3 text-start"> Call to Action / Obtaining Consent <span className="text-danger">*</span></h4>

          <div className="bg-secondary p-3 rounded-3 mt-4 bg-opacity-10 border border-color-secondary border-2">
            <div className="message-section show">
              <label className="message-label m-0 fw-semibold">
                Call to Action / Obtaining Consent  Message (Minimum 40 Characters): <span className="text-danger">*</span>
              </label>

              <Controller
                name="campaign_details.camp_desc.consent_msg"
                control={control}
                rules={{
                  required: "Call to Action / Obtaining Consent is required",
                  minLength: {
                    value: 40,
                    message: "Call to Action / Obtaining Consent must be at least 40 characters",
                  },
                }}
                render={({ field }) => {
                  const safeValue = typeof field.value === "string" ? field.value : "";
                  const length = safeValue.trim().length;
                  return (
                    <>
                      <textarea
                        className="message-textarea w-100 mt-2"
                        placeholder="Describe how consent is obtained, including opt-out instructions."
                        maxLength={2048}
                        rows={4}
                        value={safeValue}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      <div className="d-flex justify-content-between mt-2 small text-muted">
                        <span>
                          Minimum 40 characters{" "}
                          {length < 40 ? `(need ${40 - length} more)` : "✓"}
                        </span>
                        <span>{length} characters</span>
                      </div>
                    </>
                  );
                }}
              />

              {errors?.campaign_details?.camp_desc?.consent_msg && (
                <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                  {errors.campaign_details.camp_desc.consent_msg.message}
                </div>
              )}
            </div>
          </div>

          <div className="bg-secondary p-3 rounded-3 mt-4 bg-opacity-10 border border-color-secondary border-2">
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
              <div className="w-100">
                <label className="message-label m-0 fw-semibold">
                  Does your website collect Phone numbers?
                </label>
                {/* <p className="small text-muted">
                  Message & data rates may apply. Reply STOP to opt-out of
                  further messaging. Reply HELP for more information.
                </p> */}
                <p className="text-muted mt-1 mb-2">
                  {flowChecked ? campaignMisc?.staticTexts?.msg_flow_chck_yes : campaignMisc?.staticTexts?.msg_flow_chck_no}
                </p>
                <p className="text-muted">
                  <b>Note: </b>
                  The above text will be appended with the Call to Action / Obtaining Consent Message
                </p>
              </div>
              <Controller
                name="campaign_details.camp_desc.msg_flow_chk"
                control={control}
                render={({ field }) => (
                  <div className="w-20 d-flex justify-content-end">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={Boolean(field.value)}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          if (e.target.checked) {
                            setwebsitecollectModal(true);
                          }
                        }}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                )}
              />
            </div>
          </div>
        </div>





        <MDBModal open={websitecollectModal} onClose={() => setwebsitecollectModal(false)} tabIndex='-1'>
          <MDBModalDialog size='lg'>
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>Alert</MDBModalTitle>
                <MDBBtn className='btn-close' color='none' onClick={toggleOpen}></MDBBtn>
              </MDBModalHeader>
              <MDBModalBody>
                <div>
                  <p className="pb-3">Please ensure that the following entire statement is in clear view on your website</p>
                  <h6 className="my-2 text-dark" style={{fontSize:'15px'}}>SMS Disclaimer:</h6>
                  <p>By providing a telephone number and submitting the form you are consenting to be contacted by SMS text message (our message frequency varies greatly). Message & data rates may apply. Reply STOP to opt-out of all text messaging. Reply HELP for more information. See our Privacy Policy.</p>
                </div>
              </MDBModalBody>

              <MDBModalFooter>
                <MDBBtn onClick={() => setwebsitecollectModal(false)}>OK</MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

        {/* Text Field Configurations */}
        <div>
          {textFieldConfigs.map(({ id, title, minLen, path, maxLen, display }) => {
            const editing = activeEditor === id;
            const value = watch(path) || "";
            const length = value.trim().length;

            return (
              <div
                key={id}
                className="bg-secondary p-3 rounded-3 mt-4 bg-opacity-10 border border-color-secondary border-2"
              >
                <div className="message-section show">
                  <div className="message-header d-flex justify-content-between align-items-start">
                    <label className="message-label m-0 fw-semibold">
                      {title}{id === "desc" && <span className="text-danger"> *</span>}
                    </label>
                  </div>

                  <Controller
                    name={path}
                    control={control}
                    rules={{
                      required: `${display} is required`,
                      minLength: {
                        value: minLen,
                        message: `${display} must be at least ${minLen} characters`,
                      },
                    }}
                    render={({ field }) => (
                      <textarea
                        disabled={id !== "desc"}
                        className="message-textarea w-100 mt-2"
                        placeholder={id === "desc" && !isDescFocused ? descPlaceholder : ""}
                        maxLength={maxLen}
                        rows={id !== "desc" ? 2 : 6}
                        {...field}
                        onFocus={id === "desc" ? () => setIsDescFocused(true) : undefined}
                        onBlur={id === "desc" ? (e) => { field.onBlur(e); setIsDescFocused(false); } : undefined}
                      />
                    )}
                  />

                  {![
                    "Opt-In Process Description",
                    "Opt-Out Process Description",
                    "Help Process Description",
                  ].includes(display) && (
                      <div className="d-flex justify-content-between mt-2 small text-muted">
                        <span>
                          Minimum {minLen} characters{" "}
                          {length < minLen ? `(need ${minLen - length} more)` : "✓"}
                        </span>
                        <span>{length} characters</span>
                      </div>
                    )}

                  {errors?.campaign_details?.camp_desc?.[id] && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875rem",
                        marginTop: "0.25rem",
                      }}
                    >
                      {errors.campaign_details.camp_desc[id].message}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

SmsCampaignForm.displayName = "SmsCampaignForm";

export default SmsCampaignForm;